/**
 * React hook для управления клиентами
 */

import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { Client, ClientCreate, ClientUpdate, PaginationParams, ClientWeight, ClientWeightCreate, WorkoutData, ExerciseData, BodyMeasurement, BodyMeasurementCreate, MeasurementKey } from '../types';
import React from 'react';

export const useClients = (params: PaginationParams = {}) => {
  const queryClient = useQueryClient();

  // Запрос списка клиентов
  const {
    data: clients = [],
    isLoading,
    error,
    refetch
  } = useQuery(
    ['clients', params],
    () => apiService.getClients(params),
    {
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при загрузке клиентов';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для создания клиента
  const createClientMutation = useMutation(
    (data: ClientCreate) => apiService.createClient(data),
    {
      onSuccess: () => {
        toast.success('Клиент создан успешно!');
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при создании клиента';
        toast.error(errorMessage);
      }
    }
  );

  // Функция для создания клиента с кастомным обработчиком успеха
  const createClientWithCallback = (data: ClientCreate, onSuccess?: () => void) => {
    createClientMutation.mutate(data, {
      onSuccess: () => {
        toast.success('Клиент создан успешно!');
        queryClient.invalidateQueries('clients');
        if (onSuccess) {
          onSuccess();
        }
      }
    });
  };

  // Мутация для обновления клиента
  const updateClientMutation = useMutation(
    ({ id, data }: { id: number; data: ClientUpdate }) => 
      apiService.updateClient(id, data),
    {
      onSuccess: () => {
        toast.success('Клиент обновлен успешно!');
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при обновлении клиента';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для удаления клиента
  const deleteClientMutation = useMutation(
    (id: number) => apiService.deleteClient(id),
    {
      onSuccess: () => {
        toast.success('Клиент удален успешно!');
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при удалении клиента';
        toast.error(errorMessage);
      }
    }
  );

  return {
    // Данные
    clients,
    isLoading,
    error,
    
    // Действия
    createClient: createClientMutation.mutate,
    createClientWithCallback,
    updateClient: updateClientMutation.mutate,
    deleteClient: deleteClientMutation.mutate,
    refetch,
    
    // Состояния мутаций
    isCreateLoading: createClientMutation.isLoading,
    isUpdateLoading: updateClientMutation.isLoading,
    isDeleteLoading: deleteClientMutation.isLoading,
  };
};

// Хук для работы с одним клиентом
export const useClient = (id: number) => {
  const queryClient = useQueryClient();

  const {
    data: client,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['client', id],
    () => apiService.getClient(id),
    {
      enabled: !!id,
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при загрузке клиента';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для обновления клиента
  const updateClientMutation = useMutation(
    (data: ClientUpdate) => apiService.updateClient(id, data),
    {
      onSuccess: () => {
        toast.success('Клиент обновлен успешно!');
        queryClient.invalidateQueries(['client', id]);
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при обновлении клиента';
        toast.error(errorMessage);
      }
    }
  );

  return {
    // Данные
    client,
    isLoading,
    error,
    
    // Действия
    updateClient: updateClientMutation.mutate,
    refetch,
    
    // Состояния мутаций
    isUpdateLoading: updateClientMutation.isLoading,
  };
}; 

export const useClientWeights = (clientId: number) => {
  const queryClient = useQueryClient();

  const {
    data: weights = [],
    isLoading,
    error,
    refetch
  } = useQuery(['client-weights', clientId], () => apiService.getClientWeights(clientId), {
    enabled: !!clientId,
    onError: (error: any) => { 
      const errorMessage = error.detail || 'Ошибка при загрузке веса клиента';
      toast.error(errorMessage); 
    },
          onSuccess: (data) => {
        // Данные успешно загружены
      },
  });

  const addWeightMutation = useMutation(
    (data: ClientWeightCreate) => apiService.addClientWeight(clientId, data),
    {
      onMutate: async (variables) => {
        // Сохраняем количество записей до операции
        const weightsCountBefore = weights.length;
        const inputDate = new Date(variables.date).toISOString().slice(0, 10);
        const existingWeight = weights.find((w: any) => {
          const weightDate = new Date(w.date).toISOString().slice(0, 10);
          return weightDate === inputDate;
        });
        
        return { weightsCountBefore, existingWeight };
      },
      onSuccess: (response, variables, context) => {
        const existingWeight = context?.existingWeight;
        
        // Проверяем, есть ли запись с таким же ID в текущих данных
        const existingWeightById = weights.find((w: any) => w.id === response.id);
        
        // Если ID ответа совпадает с ID существующей записи, значит это обновление
        const isUpdate = existingWeightById !== undefined;
        
        if (isUpdate) {
          toast.success(`Вес обновлен: ${existingWeightById.weight} кг → ${variables.weight} кг`);
        } else {
          toast.success('Вес добавлен!');
        }
        queryClient.invalidateQueries(['client-weights', clientId]);
      },
      onError: (error: any) => { 
        const errorMessage = error.detail || 'Ошибка при добавлении веса';
        toast.error(errorMessage); 
      },
    }
  );

  const deleteWeightMutation = useMutation(
    (weightId: number) => apiService.deleteClientWeight(clientId, weightId),
    {
      onSuccess: () => {
        toast.success('Запись веса удалена');
        queryClient.invalidateQueries(['client-weights', clientId]);
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при удалении записи веса';
        toast.error(errorMessage);
      },
    }
  );

  return {
    weights,
    isLoading,
    error,
    refetch,
    addWeight: addWeightMutation.mutate,
    isAddLoading: addWeightMutation.isLoading,
    deleteWeight: deleteWeightMutation.mutate,
  };
}; 

// Хук для замеров тела клиента (шея, грудь, талия, бедра)
export const useClientBodyMeasurements = (clientId: number) => {
  const queryClient = useQueryClient();

  const {
    data: measurements = [],
    isLoading,
    error,
    refetch
  } = useQuery(
    ['client-body-measurements', clientId], 
    () => apiService.getClientBodyMeasurements(clientId), 
    {
      enabled: !!clientId,
      onError: (error: any) => { 
        const errorMessage = error.detail || 'Ошибка при загрузке замеров тела клиента';
        toast.error(errorMessage); 
      },
    }
  );

  const addMeasurementMutation = useMutation(
    (data: BodyMeasurementCreate) => apiService.addClientBodyMeasurement(clientId, data),
    {
      onSuccess: () => {
        toast.success('Замеры сохранены!');
        queryClient.invalidateQueries(['client-body-measurements', clientId]);
      },
      onError: (error: any) => { 
        const errorMessage = error.detail || 'Ошибка при сохранении замеров';
        toast.error(errorMessage); 
      },
    }
  );

  const deleteMeasurementMutation = useMutation(
    (measurementId: number) => apiService.deleteClientBodyMeasurement(clientId, measurementId),
    {
      onSuccess: () => {
        toast.success('Замеры удалены');
        queryClient.invalidateQueries(['client-body-measurements', clientId]);
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при удалении замеров';
        toast.error(errorMessage);
      },
    }
  );

  // Набор отслеживаемых замеров хранится в карточке клиента
  const updateTrackedMutation = useMutation(
    (tracked: MeasurementKey[]) => apiService.updateClient(clientId, { tracked_measurements: tracked }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client', clientId]);
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при сохранении настройки замеров';
        toast.error(errorMessage);
      },
    }
  );

  return {
    measurements,
    isLoading,
    error,
    refetch,
    addMeasurement: addMeasurementMutation.mutate,
    isAddLoading: addMeasurementMutation.isLoading,
    deleteMeasurement: deleteMeasurementMutation.mutate,
    updateTracked: updateTrackedMutation.mutateAsync,
  };
};

export const useClientWorkoutsStats = (clientId: number) => {
  const { data: workoutsStats, isLoading, error } = useQuery(
    ['clientWorkoutsStats', clientId],
    () => apiService.getClientWorkoutsStats(clientId),
    {
      enabled: !!clientId,
      staleTime: 5 * 60 * 1000, // 5 минут
    }
  );

  return {
    workoutsStats: workoutsStats || [],
    isLoading,
    error,
  };
};

export const useClientExercisesStats = (clientId: number) => {
  const { data: exercisesStats, isLoading, error } = useQuery(
    ['clientExercisesStats', clientId],
    () => apiService.getClientExercisesStats(clientId),
    {
      enabled: !!clientId,
      staleTime: 5 * 60 * 1000, // 5 минут
    }
  );
  return {
    exercisesStats: exercisesStats || [],
    isLoading,
    error,
  };
};

export const useClientExercisesWithWeight = (clientId: number) => {
  const { data: exercises, isLoading, error } = useQuery(
    ['clientExercisesWithWeight', clientId],
    () => apiService.getClientExercisesWithWeight(clientId),
    {
      enabled: !!clientId,
      staleTime: 5 * 60 * 1000, // 5 минут
    }
  );
  
  console.log('useClientExercisesWithWeight - clientId:', clientId);
  console.log('useClientExercisesWithWeight - exercises:', exercises);
  console.log('useClientExercisesWithWeight - isLoading:', isLoading);
  console.log('useClientExercisesWithWeight - error:', error);
  
  return {
    exercises: exercises || [],
    isLoading,
    error,
  };
};

export const useClientExerciseProgress = (clientId: number, exerciseId: number) => {
  const { data: progress, isLoading, error } = useQuery(
    ['clientExerciseProgress', clientId, exerciseId],
    () => apiService.getClientExerciseProgress(clientId, exerciseId),
    {
      enabled: !!clientId && !!exerciseId,
      staleTime: 5 * 60 * 1000, // 5 минут
    }
  );
  
  console.log('useClientExerciseProgress - clientId:', clientId);
  console.log('useClientExerciseProgress - exerciseId:', exerciseId);
  console.log('useClientExerciseProgress - progress:', progress);
  console.log('useClientExerciseProgress - isLoading:', isLoading);
  console.log('useClientExerciseProgress - error:', error);
  
  return {
    progress: progress || [],
    isLoading,
    error,
  };
};

export const useClientBestProgressExercise = (clientId: number) => {
  const { data: bestExercise, isLoading, error } = useQuery(
    ['clientBestProgressExercise', clientId],
    () => apiService.getClientBestProgressExercise(clientId),
    {
      enabled: !!clientId,
      staleTime: 5 * 60 * 1000, // 5 минут
    }
  );
  
  console.log('useClientBestProgressExercise - clientId:', clientId);
  console.log('useClientBestProgressExercise - bestExercise:', bestExercise);
  console.log('useClientBestProgressExercise - isLoading:', isLoading);
  console.log('useClientBestProgressExercise - error:', error);
  
  return {
    bestExercise,
    isLoading,
    error,
  };
}; 